<template>
  <card>
    <div slot="header" class="row align-items-center">
      <div class="col">
        <h6 class="text-uppercase text-muted ls-1 mb-1">Visualizar dados</h6>
        <h5 class="h3 mb-0">Top 20</h5>
      </div>
    </div>
    <div class="chart-container" v-loading="loading">
      <div ref="chartRef" class="chart"></div>
    </div>
  </card>
</template>

<script setup>
import { onMounted, ref, onBeforeUnmount, nextTick } from 'vue';
import * as echarts from 'echarts';
import axios from "@/backend/axios/index.js";

const chartRef = ref(null);
const managers = ref([]);
const loading = ref(false);
const expandedManagers = ref(null);
const expandedSalesman = ref(null);
let myChart = null;

const resizeChart = ()=> {
  if (myChart) {
    myChart.resize();
  }
}

const setupResizeObserver = () => {
  const resizeObserver = new ResizeObserver(resizeChart);
  if (chartRef.value) {
    resizeObserver.observe(chartRef.value);
  }
  return resizeObserver;
};

const fetchManagers = async () => {
  loading.value = true;
  try {
    const response = await axios.get('/api/v1/report/sales_ranking');
    managers.value = response.data;
    
    expandedManagers.value = managers.value[0].manager;
    if (managers.value[0].salesmen.length > 0) {
      expandedSalesman.value = managers.value[0].salesmen[0].salesman;
    }
    
    await nextTick();
    initChart();
  } catch (error) {
    console.error("Erro ao buscar managers:", error);
  } finally {
    loading.value = false;
  }
};

const formatCurrency = (value) => {
  return (parseFloat(value) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

const getTreeData = () => {
  return [
    {
      name: "Gestores",
      symbol: 'circle',
      children: managers.value.map(manager => ({
        name: manager.manager,
        tooltip: { formatter: `Faturamento Gerado: ${formatCurrency(manager.total_value_manager)}` },
        symbol: 'circle',
        children: expandedManagers.value === manager.manager
          ? manager.salesmen.map(salesman => ({
              name: salesman.salesman,
              tooltip: { formatter: `Faturamento Gerado: ${formatCurrency(salesman.total_value_salesman)}` },
              symbol: 'circle',
              children: expandedSalesman.value === salesman.salesman
                ? [
                    {
                      name: "📦",
                      tooltip: { 
                        formatter: `Faturamento Gerado: ${formatCurrency(salesman.products.reduce((sum, c) => sum + c.total_value_product, 0))}`
                      },
                      symbol: 'circle',
                      children: salesman.products.map(product => ({
                        name: product.product ? product.product : "Produto sem nome",
                        tooltip: { 
                          formatter: `Faturamento Gerado: ${formatCurrency(product.total_value_product)}<br>
                                      Quantidade: ${product.total_quantity_product.toLocaleString("pt-BR")}`
                        },
                        symbol: 'none',
                      }))
                    },
                    {
                      name: "👤",
                      tooltip: { 
                        formatter: `Faturamento Gerado: ${formatCurrency(salesman.clients.reduce((sum, c) => sum + c.total_value_client, 0))}`
                      },
                      symbol: 'circle',
                      children: salesman.clients.map(client => ({
                        name: client.client ? client.client : "Cliente sem Nome",
                        tooltip: { 
                          formatter: `Faturamento Gerado: ${formatCurrency(client.total_value_client)}<br>
                                      Quantidade: ${client.total_quantity_client.toLocaleString("pt-BR")}`
                        },
                        symbol: 'none'
                      }))
                    },
                  ]
                : []
            }))
          : []
      }))
    }
  ];
};

const updateChart = () => {
  if (!myChart) return;

  myChart.setOption({
    series: [{ data: getTreeData() }]
  });
};

const initChart = () => {
  if (!chartRef.value) return;

  if (myChart) {
    myChart.dispose();
  }

  myChart = echarts.init(chartRef.value);

  const option = {
    tooltip: {
      trigger: 'item',
      triggerOn: 'mousemove',
      backgroundColor: '#2dce89',
      textStyle: { 
        color: '#ffffff',
        fontWeight: 'bold',
        fontSize: 14
      },
      formatter: params => params.data.tooltip ? params.data.tooltip.formatter : params.name
    },
    series: [
      {
        type: 'tree',
        data: getTreeData(),
        top: '1%',
        left: '6%',
        bottom: '1%',
        right: '28%',
        symbolSize: 14,
        itemStyle: {
          color: '#2dce89',
          borderColor: '#ffffff',
          borderWidth: 2,
        },
        label: {
          position: 'left',
          verticalAlign: 'middle',
          align: 'right',
          fontSize: 12,
          fontSize: window.innerWidth < 768 ? 10 : 12
        },
        leaves: {
          label: { 
            position: 'right',
            verticalAlign: 'middle',
            align: 'left',
            fontSize: window.innerWidth < 768 ? 10 : 12
          }
        },
        lineStyle: {
          curveness: 0.5,
          color: '#2dce89',
          borderWidth: 1,
        },
        expandAndCollapse: false,
        initialTreeDepth: 4,
      }
    ],
    responsive: true,
    media: [
      {
        query: {
          maxWidth: 768
        },
        option: {
          series: [{
            right: '20%',
            label: {
              fontSize: 10
            },
            leaves: {
              label: {
                fontSize: 10
              }
            }
          }]
        }
      },
      {
        query: {
          maxWidth: 480
        },
        option: {
          series: [{
            right: '15%',
            label: {
              fontSize: 8
            },
            leaves: {
              label: {
                fontSize: 8
              }
            }
          }]
        }
      }
    ]
  };

  myChart.setOption(option);

  myChart.on('click', function (params) {
    if (!params.data.children) return;

    const clickedNode = params.data.name;

    if (managers.value.some(m => m.manager === clickedNode)) {
      expandedManagers.value = expandedManagers.value === clickedNode ? null : clickedNode;
      expandedSalesman.value = null;
    } else if (managers.value.some(m => m.salesmen.some(s => s.salesman === clickedNode))) {
      expandedSalesman.value = expandedSalesman.value === clickedNode ? null : clickedNode;
    }

  updateChart();
});

  const resizeObserver = setupResizeObserver();

  window.addEventListener('resize', resizeChart);
};

onMounted(() => {
  fetchManagers();
});

onBeforeUnmount(() => {
  if (myChart) {
    myChart.dispose();
    myChart = null;
  }
  window.removeEventListener('resize', resizeChart);
});
</script>

<style scoped>
.chart-container {
  position: relative;
  width: 100%;
  height: 100%;
  max-height: 100%;
  min-height: 400px;
}

.chart {
  width: 100%;
  max-height: 100%;
  min-height: 800px;
}

@media (max-width: 768px) {
  .chart {
    min-height: 300px;
  }
}

@media (max-width: 480px) {
  .chart {
    min-height: 250px;
  }
}
</style>

